import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Input, theme } from "antd";
import { CheckOutlined } from "@ant-design/icons";

/**
 * ColorWheel — hue/saturation wheel + lightness slider + HEX input.
 *
 * Color model: HSL. Reversible hex <-> HSL.
 *   - Wheel: hue = angle (0..360deg), saturation = radius (0..100%).
 *     A radial white overlay fades saturation toward the center (white core),
 *     matching the mockup's picker look.
 *   - Slider: lightness 0..100, 3-stop gradient black -> pure color -> white.
 *   - HEX input: validates #RRGGBB, commits on blur / Enter.
 *
 * Interaction: Pointer Events API (pointerdown + setPointerCapture +
 * pointermove with e.buttons === 1). Works for mouse, touch and pen.
 *
 * Accessibility: wheel and slider are role="slider", focusable, arrow keys
 * adjust (wheel: arrows = hue +/-, slider: arrows = lightness +/-).
 */

type HSL = { h: number; s: number; l: number };

// --- color conversions ------------------------------------------------------

function clamp(n: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, n));
}

function normalizeHex(hex: string): string | null {
    let v = hex.trim();
    if (!v.startsWith("#")) v = "#" + v;
    if (/^#([0-9a-fA-F]{6})$/.test(v)) return v.toLowerCase();
    if (/^#([0-9a-fA-F]{3})$/.test(v)) {
        v = "#" + v.slice(1).split("").map((c) => c + c).join("");
        return v.toLowerCase();
    }
    return null;
}

function hexToHsl(hex: string): HSL {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h = 0;
    let s = 0;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = ((g - b) / d + (g < b ? 6 : 0)) * 60; break;
            case g: h = ((b - r) / d + 2) * 60; break;
            case b: h = ((r - g) / d + 4) * 60; break;
        }
    }
    return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function hslToHex(h: number, s: number, l: number): string {
    const sn = s / 100;
    const ln = l / 100;
    const c = (1 - Math.abs(2 * ln - 1)) * sn;
    const hp = h / 60;
    const x = c * (1 - Math.abs((hp % 2) - 1));
    let r = 0, g = 0, b = 0;
    if (hp >= 0 && hp < 1) { r = c; g = x; }
    else if (hp < 2) { r = x; g = c; }
    else if (hp < 3) { g = c; b = x; }
    else if (hp < 4) { g = x; b = c; }
    else if (hp < 5) { r = x; b = c; }
    else { r = c; b = x; }
    const m = ln - c / 2;
    const toHex = (n: number) =>
        Math.round((n + m) * 255).toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// --- component --------------------------------------------------------------

interface ColorWheelProps {
    /** Current color as #RRGGBB */
    value: string;
    /** Fired with #RRGGBB whenever the user changes the color */
    onChange: (hex: string) => void;
    /** Wheel diameter in px (default 180) */
    size?: number;
}

const ColorWheel: React.FC<ColorWheelProps> = ({ value, onChange, size = 180 }) => {
    const { token } = theme.useToken();
    const wheelRef = useRef<HTMLDivElement>(null);
    const sliderRef = useRef<HTMLDivElement>(null);

    const initial = useMemo(() => {
        const norm = normalizeHex(value) ?? "#1890ff";
        return hexToHsl(norm);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const [hsl, setHsl] = useState<HSL>(initial);
    const [hexInput, setHexInput] = useState<string>((normalizeHex(value) ?? "#1890ff").toUpperCase());

    // External value changes -> sync internal state
    useEffect(() => {
        const norm = normalizeHex(value);
        if (!norm) return;
        const next = hexToHsl(norm);
        setHsl((prev) =>
            prev.h === next.h && prev.s === next.s && prev.l === next.l ? prev : next
        );
        setHexInput(norm.toUpperCase());
    }, [value]);

    const commit = useCallback((next: HSL) => {
        setHsl(next);
        const hex = hslToHex(next.h, next.s, next.l);
        setHexInput(hex.toUpperCase());
        onChange(hex);
    }, [onChange]);

    // --- wheel pointer math -------------------------------------------------
    const updateFromWheel = useCallback((clientX: number, clientY: number) => {
        const el = wheelRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = clientX - cx;
        const dy = clientY - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const radius = rect.width / 2;
        // CSS conic-gradient convention: 0deg = top, clockwise.
        // atan2(dx, -dy) gives that (y is inverted in screen coords).
        let angle = (Math.atan2(dx, -dy) * 180) / Math.PI; // -180..180
        if (angle < 0) angle += 360;                        // 0..360
        const s = clamp((dist / radius) * 100, 0, 100);
        commit({ h: Math.round(angle), s: Math.round(s), l: hsl.l });
    }, [commit, hsl.l]);

    const onWheelPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        e.preventDefault();
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        updateFromWheel(e.clientX, e.clientY);
    };
    const onWheelPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (e.buttons !== 1) return;
        updateFromWheel(e.clientX, e.clientY);
    };

    // --- slider pointer math ------------------------------------------------
    const updateFromSlider = useCallback((clientX: number) => {
        const el = sliderRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
        commit({ ...hsl, l: Math.round(ratio * 100) });
    }, [commit, hsl]);

    const onSliderPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        e.preventDefault();
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        updateFromSlider(e.clientX);
    };
    const onSliderPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
        if (e.buttons !== 1) return;
        updateFromSlider(e.clientX);
    };

    // --- keyboard -----------------------------------------------------------
    const onWheelKeyDown = (e: React.KeyboardEvent) => {
        const step = e.shiftKey ? 10 : 1;
        let next: HSL | null = null;
        switch (e.key) {
            case "ArrowLeft":  next = { ...hsl, h: (hsl.h - step + 360) % 360 }; break;
            case "ArrowRight": next = { ...hsl, h: (hsl.h + step) % 360 }; break;
            case "ArrowUp":    next = { ...hsl, s: clamp(hsl.s + step, 0, 100) }; break;
            case "ArrowDown":  next = { ...hsl, s: clamp(hsl.s - step, 0, 100) }; break;
            default: return;
        }
        e.preventDefault();
        commit(next);
    };

    const onSliderKeyDown = (e: React.KeyboardEvent) => {
        const step = e.shiftKey ? 10 : 1;
        let next: HSL | null = null;
        switch (e.key) {
            case "ArrowLeft":  next = { ...hsl, l: clamp(hsl.l - step, 0, 100) }; break;
            case "ArrowRight":
            case "ArrowUp":    next = { ...hsl, l: clamp(hsl.l + step, 0, 100) }; break;
            case "ArrowDown":  next = { ...hsl, l: clamp(hsl.l - step, 0, 100) }; break;
            default: return;
        }
        e.preventDefault();
        commit(next);
    };

    // --- thumb positions ----------------------------------------------------
    const radius = size / 2;
    const wheelThumbAngle = (hsl.h * Math.PI) / 180;
    const wheelThumbDist = (hsl.s / 100) * (radius - 12); // 12px inner padding for thumb
    // CSS conic-gradient convention: 0deg = top, clockwise.
    // Screen y is inverted, so: x = sin(a), y = -cos(a).
    const wheelThumbX = radius + Math.sin(wheelThumbAngle) * wheelThumbDist - 10;
    const wheelThumbY = radius - Math.cos(wheelThumbAngle) * wheelThumbDist - 10;

    const sliderThumbX = `calc(${hsl.l}% - 8px)`;
    // pureHex (L=50) is the correct midpoint for the lightness gradient track.
    // actualHex carries the real selected lightness — used for thumbs and swatches.
    const pureHex = hslToHex(hsl.h, hsl.s, 50);
    const actualHex = hslToHex(hsl.h, hsl.s, hsl.l);

    // --- hex input ----------------------------------------------------------
    const onHexCommit = () => {
        const norm = normalizeHex(hexInput);
        if (norm) {
            commit(hexToHsl(norm));
        } else {
            setHexInput((normalizeHex(value) ?? "#1890ff").toUpperCase());
        }
    };

    // --- inline styles (dark-mode aware via tokens) -------------------------
    const wrapperStyle: React.CSSProperties = {
        backgroundColor: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
        padding: 16,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    };

    const wheelStyle: React.CSSProperties = {
        position: "relative",
        width: size,
        height: size,
        borderRadius: "50%",
        cursor: "crosshair",
        touchAction: "none",
        background:
            // hue ring via conic-gradient + saturation fade via radial white overlay
            "radial-gradient(circle, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0) 70%)," +
            "conic-gradient(from 0deg," +
            "#ff0000 0deg, #ffff00 60deg, #00ff00 120deg, #00ffff 180deg," +
            "#0000ff 240deg, #ff00ff 300deg, #ff0000 360deg)",
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)",
    };

    const wheelThumbStyle: React.CSSProperties = {
        position: "absolute",
        width: 20,
        height: 20,
        borderRadius: "50%",
        backgroundColor: actualHex,
        border: `3px solid #fff`,
        boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
        left: wheelThumbX,
        top: wheelThumbY,
        transform: "scale(1)",
        transition: "transform 0.12s ease",
        pointerEvents: "none",
    };

    const sliderTrackStyle: React.CSSProperties = {
        position: "relative",
        width: "100%",
        height: 12,
        borderRadius: 999,
        cursor: "pointer",
        touchAction: "none",
        background: `linear-gradient(to right, #000, ${pureHex}, #fff)`,
        boxShadow: "inset 0 1px 2px rgba(0,0,0,0.15)",
    };

    const sliderThumbStyle: React.CSSProperties = {
        position: "absolute",
        width: 16,
        height: 16,
        borderRadius: "50%",
        backgroundColor: "#fff",
        border: `1px solid ${token.colorBorder}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
        left: sliderThumbX,
        top: "50%",
        transform: "translateY(-50%)",
        pointerEvents: "none",
    };

    return (
        <div style={wrapperStyle}>
            {/* Wheel */}
            <div
                ref={wheelRef}
                style={wheelStyle}
                role="slider"
                tabIndex={0}
                aria-label="Hue and saturation"
                aria-valuemin={0}
                aria-valuemax={360}
                aria-valuenow={hsl.h}
                onPointerDown={onWheelPointerDown}
                onPointerMove={onWheelPointerMove}
                onKeyDown={onWheelKeyDown}
            >
                <div style={wheelThumbStyle} />
            </div>

            {/* Lightness slider */}
            <div style={{ width: "100%", marginTop: 16 }}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 10,
                        color: token.colorTextTertiary,
                        fontWeight: 500,
                        padding: "0 4px",
                        marginBottom: 4,
                    }}
                >
                    <span>DARK</span>
                    <span>LIGHT</span>
                </div>
                <div
                    ref={sliderRef}
                    style={sliderTrackStyle}
                    role="slider"
                    tabIndex={0}
                    aria-label="Lightness"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={hsl.l}
                    onPointerDown={onSliderPointerDown}
                    onPointerMove={onSliderPointerMove}
                    onKeyDown={onSliderKeyDown}
                >
                    <div style={sliderThumbStyle} />
                </div>
            </div>

            {/* HEX display + input */}
            <div
                style={{
                    width: "100%",
                    marginTop: 16,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor: token.colorFillQuaternary,
                    borderRadius: token.borderRadius,
                    padding: "10px 12px",
                    border: `1px solid ${token.colorBorderSecondary}`,
                }}
            >
                <span style={{ fontSize: 12, color: token.colorTextTertiary, fontWeight: 500 }}>
                    HEX
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div
                        style={{
                            width: 12,
                            height: 12,
                            borderRadius: 3,
                            backgroundColor: actualHex,
                            border: `1px solid ${token.colorBorder}`,
                        }}
                    />
                    <Input
                        variant="borderless"
                        value={hexInput}
                        onChange={(e) => setHexInput(e.target.value)}
                        onBlur={onHexCommit}
                        onPressEnter={onHexCommit}
                        size="small"
                        style={{
                            width: 90,
                            fontFamily: "monospace",
                            letterSpacing: 1,
                            color: token.colorText,
                            textTransform: "uppercase",
                            padding: 0,
                            background: "transparent",
                        }}
                        suffix={
                            normalizeHex(hexInput) ? (
                                <CheckOutlined style={{ color: token.colorSuccess, fontSize: 12 }} />
                            ) : null
                        }
                    />
                </div>
            </div>
        </div>
    );
};

export default ColorWheel;
